import axios from "axios";
import { hideChildModal, hideModal } from "./modal";
import { hideDialog } from "./dialog";

/*****************/
/* INITIAL STATE */
/*****************/
const initialState = {
  loading: false,
  error: "",
  data: [],
  query: { ids: [], total: null },
};

/*********/
/* TYPES */
/*********/
const FETCH_SELECTED_TERMINAL_CALLS = "FETCH_SELECTED_TERMINAL_CALLS";
const FETCH_SELECTED_TERMINAL_CALLS_SUCCESS = "FETCH_SELECTED_TERMINAL_CALLS_SUCCESS";
const FETCH_SELECTED_TERMINAL_CALLS_ERROR = "FETCH_SELECTED_TERMINAL_CALLS_ERROR";
const FETCH_NEXT_SELECTED_TERMINAL_CALLS = "FETCH_NEXT_SELECTED_TERMINAL_CALLS";
const FETCH_NEXT_SELECTED_TERMINAL_CALLS_SUCCESS = "FETCH_NEXT_SELECTED_TERMINAL_CALLS_SUCCESS";

const UPDATE_SELECTED_TERMINAL_CALL = "UPDATE_SELECTED_TERMINAL_CALL";
const UPDATE_SELECTED_TERMINAL_CALL_SUCCESS = "UPDATE_SELECTED_TERMINAL_CALL_SUCCESS";
const UPDATE_SELECTED_TERMINAL_CALL_ERROR = "UPDATE_SELECTED_TERMINAL_CALL_ERROR";

const REFRESH_TERMINAL_CALL = "REFRESH_TERMINAL_CALL";

const CREATE_SELECTED_TERMINAL_CALL = "CREATE_SELECTED_TERMINAL_CALL";
const CREATE_SELECTED_TERMINAL_CALL_SUCCESS = "CREATE_SELECTED_TERMINAL_CALL_SUCCESS";
const CREATE_SELECTED_TERMINAL_CALL_ERROR = "CREATE_SELECTED_TERMINAL_CALL_ERROR";

/**************/
/* UTILITIES */
/**************/
const getIdParams = (ids) =>
  ids.length > 0 ? ids.map((id) => `&id[]=${id}`).join("") : "";

/*******************/
/* ACTION CREATORS */
/*******************/
export const getSelectedTerminalCalls =
  (ids = [], options = {}) =>
  async (dispatch, getState) => {
    const validIds = ids.filter((id) => {
      if (typeof id === "string" && id.trim() !== "") {
        return true;
      }

      if (typeof id === "number" && Number.isInteger(id)) {
        return true;
      }
      return false;
    });

    if (validIds.length === 0) {
      dispatch({
        type: FETCH_SELECTED_TERMINAL_CALLS_SUCCESS,
        payload: { selectedTerminalCalls: [], query: { ids: [], total: 0 } },
      });
      return;
    }

    const {
      selectedTerminalCalls: { query, data },
    } = getState();

    const { force = false } = options;
    const isSameQuery = JSON.stringify(query.ids) === JSON.stringify(validIds);
    const isDataComplete = data.length === query.total;

    if (force || !isSameQuery || !isDataComplete) {
      dispatch({ type: FETCH_SELECTED_TERMINAL_CALLS });

      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/terminal_calls?page=1${getIdParams(validIds)}`,
          {
            headers: {
              "Content-Type": "application/ld+json; charset=utf-8",
              Accept: "application/ld+json",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        const members = response.data["hydra:member"] || [];
        const total = response.data["hydra:totalItems"] || 0;

        dispatch({
          type: FETCH_SELECTED_TERMINAL_CALLS_SUCCESS,
          payload: {
            selectedTerminalCalls: members,
            query: { ids: validIds, total },
          },
        });

        const next = response.data["hydra:view"]?.["hydra:next"];
        if (next) dispatch(getNextSelectedTerminalCalls(next));
      } catch (err) {
        dispatch({ type: FETCH_SELECTED_TERMINAL_CALLS_ERROR, payload: err.message });
      }
    }
  };

export const getNextSelectedTerminalCalls = (nextUrl) => async (dispatch) => {
  dispatch({ type: FETCH_NEXT_SELECTED_TERMINAL_CALLS });

  try {
    const response = await axios.get(nextUrl, {
      headers: {
        "Content-Type": "application/ld+json; charset=utf-8",
        Accept: "application/ld+json",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });

    dispatch({
      type: FETCH_NEXT_SELECTED_TERMINAL_CALLS_SUCCESS,
      payload: response.data["hydra:member"] || [],
    });

    const next = response.data["hydra:view"]?.["hydra:next"];
    if (next) dispatch(getNextSelectedTerminalCalls(next));
  } catch (err) {
    dispatch({ type: FETCH_SELECTED_TERMINAL_CALLS_ERROR, payload: err.message });
  }
};

export const updateSelectedTerminalCall =
  (terminalCall, allowHideModal = true) =>
  async (dispatch, getState) => {
    dispatch({ type: UPDATE_SELECTED_TERMINAL_CALL });

    const {
      modal: { childModal },
    } = getState();

    try {
      const id = terminalCall["@id"].split("/").pop();

      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/terminal_calls/${id}`,
        terminalCall,
        {
          headers: {
            "Content-Type": "application/ld+json",
            Accept: "application/ld+json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      dispatch({
        type: UPDATE_SELECTED_TERMINAL_CALL_SUCCESS,
        payload: response.data,
      });

      if (allowHideModal) {
        if (childModal) {
          dispatch(hideChildModal());
        } else {
          dispatch(hideModal());
        }
      }
      dispatch(hideDialog());
    } catch (err) {
      dispatch({ type: UPDATE_SELECTED_TERMINAL_CALL_ERROR, payload: err.message });
    }
  };

export const createSelectedTerminalCall =
  (terminalCall, allowHideModal = true) =>
  async (dispatch, getState) => {
    dispatch({ type: CREATE_SELECTED_TERMINAL_CALL });

    const {
      modal: { childModal },
    } = getState();

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/terminal_calls`,
        terminalCall,
        {
          headers: {
            "Content-Type": "application/ld+json",
            Accept: "application/ld+json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      dispatch({
        type: CREATE_SELECTED_TERMINAL_CALL_SUCCESS,
        payload: response.data,
      });

      if (allowHideModal) {
        if (childModal) {
          dispatch(hideChildModal());
        } else {
          dispatch(hideModal());
        }
      }

      dispatch(hideDialog());
    } catch (err) {
      dispatch({ type: CREATE_SELECTED_TERMINAL_CALL_ERROR, payload: err.message });
    }
  };

export const refreshTerminalCall = (id) => async (dispatch) => {
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/terminal_calls/${id.replace(
        "/api/terminal_calls/",
        ""
      )}`,
      {
        headers: {
          "Content-Type": "application/ld+json; charset=utf-8",
          Accept: "application/ld+json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );

    dispatch({
      type: REFRESH_TERMINAL_CALL,
      payload: response.data,
    });
  } catch (err) {
    dispatch({ type: FETCH_SELECTED_TERMINAL_CALLS_ERROR, payload: err.message });
  }
};

export const loadSelectedTerminalCalls = () => ({ type: FETCH_SELECTED_TERMINAL_CALLS });
export const loadNextSelectedTerminalCalls = () => ({
  type: FETCH_NEXT_SELECTED_TERMINAL_CALLS,
});
export const setSelectedTerminalCallError = (msg) => ({
  type: FETCH_SELECTED_TERMINAL_CALLS_ERROR,
  payload: msg,
});

/***********/
/* REDUCER */
/***********/
const selectedTerminalCallReducer = (state = initialState, { type, payload }) => {
  switch (type) {
    case FETCH_SELECTED_TERMINAL_CALLS:
      return { ...state, loading: true, error: "" };
    case FETCH_SELECTED_TERMINAL_CALLS_SUCCESS:
      return {
        ...state,
        loading: false,
        error: "",
        data: payload.selectedTerminalCalls,
        query: payload.query,
      };
    case FETCH_SELECTED_TERMINAL_CALLS_ERROR:
      return { ...state, loading: false, error: payload };
    case FETCH_NEXT_SELECTED_TERMINAL_CALLS:
      return { ...state, error: "" };
    case FETCH_NEXT_SELECTED_TERMINAL_CALLS_SUCCESS: {
      const existingIds = new Set(state.data.map((tc) => tc["@id"]));
      const uniqueNewItems = payload.filter((tc) => !existingIds.has(tc["@id"]));

      return {
        ...state,
        loading: false,
        data: [...state.data, ...uniqueNewItems],
      };
    }
    case UPDATE_SELECTED_TERMINAL_CALL:
      return { ...state, loading: true, error: "" };
    case UPDATE_SELECTED_TERMINAL_CALL_SUCCESS:
      return {
        ...state,
        loading: false,
        data: state.data.map((tc) =>
          tc["@id"] === payload["@id"] ? payload : tc
        ),
      };
    case UPDATE_SELECTED_TERMINAL_CALL_ERROR:
      return { ...state, loading: false, error: payload };
    case REFRESH_TERMINAL_CALL: {
      const updated = state.data.map((tc) =>
        tc["@id"] === payload["@id"] ? payload : tc
      );
      const notFound = !state.data.some((tc) => tc["@id"] === payload["@id"]);
      return {
        ...state,
        data: notFound ? [...state.data, payload] : updated,
      };
    }
    case CREATE_SELECTED_TERMINAL_CALL:
      return { ...state, loading: true, error: "" };
    case CREATE_SELECTED_TERMINAL_CALL_SUCCESS: {
        const exists = state.data.some((tc) => tc["@id"] === payload["@id"]);
        const updatedData = exists
          ? state.data.map((tc) => (tc["@id"] === payload["@id"] ? payload : tc))
          : [...state.data, payload];
      
        return {
          ...state,
          loading: false,
          data: updatedData,
        };
      }
    case CREATE_SELECTED_TERMINAL_CALL_ERROR:
      return { ...state, loading: false, error: payload };
    default:
      return state;
  }
};

export default selectedTerminalCallReducer;